/**
 * Mobile burger menu open/close interactions.
 *
 *   - Click `.amri-burger-btn` → open `#amri-mobile-drawer`.
 *   - Click `[data-drawer-control="close"]` (cover overlay or close button) → close.
 *   - ESC key → close.
 *   - Body scroll locks while open (`.amri-drawer-open` class).
 */

const DRAWER_ID = 'amri-mobile-drawer';
const BODY_OPEN_CLASS = 'amri-drawer-open';

function getDrawer(): HTMLElement | null {
    return document.getElementById(DRAWER_ID);
}

export function closeMobileDrawer(): void {
    const drawer = getDrawer();
    if (!drawer) {
        return;
    }

    drawer.style.display = 'none';
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_OPEN_CLASS);
}

export function openMobileDrawer(): void {
    const drawer = getDrawer();
    if (!drawer) {
        return;
    }

    drawer.style.display = 'block';
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_OPEN_CLASS);
}

function handleDocumentClick(e: Event): void {
    const target = e.target as HTMLElement | null;
    if (!target) {
        return;
    }

    if (target.closest('.amri-burger-btn')) {
        e.preventDefault();
        openMobileDrawer();

        return;
    }

    // Accordion: tapping a nav dropdown label OR the "My account" button toggles
    // its children open/closed. The drawer reuses the desktop nav + user-menu
    // markup, both of which reveal on hover — unusable on touch — so the drawer
    // collapses them (CSS) and expands on tap here.
    const toggler = target.closest(
        '.header-dropdown-toggler, .amri-user-menu button[data-variant="secondary"]',
    );
    if (toggler && toggler.closest(`#${DRAWER_ID}`)) {
        e.preventDefault();
        toggler.closest('.header-dropdown, .amri-user-menu')?.classList.toggle('is-open');

        return;
    }

    if (target.closest('[data-drawer-control="close"]')) {
        e.preventDefault();
        closeMobileDrawer();
    }
}

function handleEscKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
        closeMobileDrawer();
    }
}

export function initMobileDrawer(): void {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleEscKey);
}
